import { useTheme as useThemeContext } from '../contexts/ThemeContext';

/**
 * Custom hook for theme management with utility functions
 *
 * @example
 * const { theme, toggleTheme, isDark, getThemeClass } = useTheme();
 *
 * // Use in components
 * <div className={getThemeClass('bg-white', 'bg-gray-800')}>
 *   Content
 * </div>
 */
export function useTheme() {
  const context = useThemeContext();

  /**
   * Returns one of two classes based on current theme
   * @param lightClass - Class to use in light mode
   * @param darkClass - Class to use in dark mode
   */
  const getThemeClass = (lightClass: string, darkClass: string): string => {
    return context.isLight ? lightClass : darkClass;
  };

  /**
   * Returns one of two values based on current theme
   * @param lightValue - Value to use in light mode
   * @param darkValue - Value to use in dark mode
   */
  const getThemeValue = <T,>(lightValue: T, darkValue: T): T => {
    return context.isLight ? lightValue : darkValue;
  };

  /**
   * Returns theme-aware color value
   * Useful for inline styles and chart configurations
   */
  const getColor = {
    text: () => getThemeValue('#1F2937', '#F9FAFB'),
    textSecondary: () => getThemeValue('#6B7280', '#D1D5DB'),
    background: () => getThemeValue('#FFFFFF', '#1F2937'),
    backgroundSecondary: () => getThemeValue('#F9FAFB', '#111827'),
    border: () => getThemeValue('#E5E7EB', '#374151'),
    primary: () => '#3B82F6', // Blue stays same in both themes
    success: () => '#10B981', // Green stays same
    warning: () => '#F59E0B', // Orange stays same
    danger: () => '#EF4444', // Red stays same
  };

  return {
    ...context,
    getThemeClass,
    getThemeValue,
    getColor,
  };
}

export default useTheme;
